import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ContentCopy, ExpandMore, DataObject } from "@mui/icons-material";
import { Timestamp } from "firebase/firestore";
import { useEvaluationDetail } from "../../hooks/useEvaluations";
import { EvaluationDetail } from "../../types";
import EvaluationDetailV2_0_0View from "./detail/EvaluationDetailV2_0_0";
import EvaluationDetailV2_1_0View from "./detail/EvaluationDetailV2_1_0";
import EvaluationDetailV2_2_0View from "./detail/EvaluationDetailV2_2_0";
import EvaluationDetailV3_0_0View from "./detail/EvaluationDetailV3_0_0";

// Firestore の Timestamp はそのまま stringify すると {seconds, nanoseconds} になり読みにくいため ISO 文字列へ変換する
const stringifyEvaluationJson = (data: unknown): string =>
  JSON.stringify(
    data,
    function (this: Record<string, unknown>, key, value) {
      const originalValue = this[key];
      if (originalValue instanceof Timestamp) {
        return originalValue.toDate().toISOString();
      }
      return value;
    },
    2
  );

const copyJsonToClipboard = async (data: unknown): Promise<boolean> => {
  try {
    const json = stringifyEvaluationJson(data);
    await navigator.clipboard.writeText(json);
    return true;
  } catch (error) {
    console.error("Failed to copy evaluation JSON to clipboard:", error);
    return false;
  }
};

interface EvaluationDetailDialogProps {
  open: boolean;
  evaluationId: string | null;
  onClose: () => void;
}

const renderBody = (evaluation: EvaluationDetail) => {
  switch (evaluation.version) {
    case "v2.0.0":
      return <EvaluationDetailV2_0_0View evaluation={evaluation} />;
    case "v2.1.0":
      return <EvaluationDetailV2_1_0View evaluation={evaluation} />;
    case "v2.2.0":
      return <EvaluationDetailV2_2_0View evaluation={evaluation} />;
    case "v3.0.0":
      return <EvaluationDetailV3_0_0View evaluation={evaluation} />;
  }
};

/**
 * 評価バージョンの表示ラベルを組み立てる。
 *
 * 詳細ビューを持つのは v2系と v3系だけで、そのレコードは必ず evaluationVersion を保存している。
 * 保存値をそのまま表示する（詳細が開けている時点で evaluationVersion は存在する）。
 */
const buildEvaluationVersionLabel = (evaluation: EvaluationDetail): string =>
  `eval v${evaluation.version.replace(/^v/, "")}`;

const EvaluationDetailDialog: React.FC<EvaluationDetailDialogProps> = ({
  open,
  evaluationId,
  onClose,
}) => {
  const { evaluation, rawEvaluationData, loading, error, fetchEvaluation } = useEvaluationDetail();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && evaluationId) {
      fetchEvaluation(evaluationId);
    }
  }, [open, evaluationId, fetchEvaluation]);

  const getTotalScoreColor = (): "success" | "primary" | "warning" | "error" => {
    if (!evaluation) return "warning";
    const score = evaluation.result.totalScore;
    if (score >= 80) return "success";
    if (score >= 60) return "primary";
    if (score >= 40) return "warning";
    return "error";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">評価詳細</Typography>
          {evaluation && (
            <>
              <Chip
                label={`総合スコア: ${evaluation.result.totalScore}点`}
                color={getTotalScoreColor()}
                size="small"
              />
            </>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {evaluation && !loading && (
          <Box>
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                評価日時
              </Typography>
              <Typography variant="body1">
                {new Date(evaluation.timestamp._seconds * 1000).toLocaleString("ja-JP")}
              </Typography>
              {/* 3つの版数は用途が違うため必ず並べて出す。
                  app: どのクライアントから来たか / eval: 採点ロジックと結果構造の版 / prompt: プロンプト文言の版 */}
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {evaluation.appVersion && (
                  <Chip label={`app v${evaluation.appVersion}`} size="small" variant="outlined" />
                )}
                <Chip
                  label={buildEvaluationVersionLabel(evaluation)}
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
                {evaluation.promptVersion !== undefined && (
                  <Chip
                    label={`prompt v${evaluation.promptVersion}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box mb={3} textAlign="center">
              <Typography variant="h2" color={`${getTotalScoreColor()}.main`} fontWeight="bold">
                {evaluation.result.totalScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / 100 点
              </Typography>
            </Box>

            {renderBody(evaluation)}
          </Box>
        )}

        {rawEvaluationData && !loading && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={1}>
                <DataObject fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  生データ（JSON）
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Firestoreに保存された評価ドキュメントそのままの内容です。採点結果の検証やLLMへの確認に使えます。
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: "grey.900",
                  color: "grey.100",
                  borderRadius: 1,
                  fontSize: 12,
                  fontFamily: "monospace",
                  overflow: "auto",
                  maxHeight: 480,
                }}
              >
                {stringifyEvaluationJson(rawEvaluationData)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<ContentCopy />}
          onClick={async () => {
            if (rawEvaluationData) {
              const success = await copyJsonToClipboard(rawEvaluationData);
              if (success) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }
          }}
          disabled={!rawEvaluationData || loading}
          color={copied ? "success" : "inherit"}
          sx={{ mr: "auto" }}
        >
          {copied ? "コピーしました" : "生JSONをコピー"}
        </Button>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluationDetailDialog;
